import { Program, Series } from 'base/api/types';
import { PK } from 'common/types';
import { useMinisUpdate } from 'modules/minis/queries';
import { useSeriesUpdate } from 'modules/series/queries';
import { useProgramUpdate } from 'modules/programs/queries';

import { BlogFields } from './consts';
import { useBlogUpdate, useBlogCreate } from './queries';
import { FeatureFlags } from 'common/utils/featureFlags';
import React from 'react';
import { fields } from './consts';

const { challenges } = FeatureFlags;

export const useSaveSeriesBlog = (pk?: PK) => {
  const { mutate, isLoading, isError, isSuccess } = useSeriesUpdate();

  const save = (blogsPks: PK[], data?: Series) => {
    if (!pk) {
      return null;
    }

    const blogPk = blogsPks.length >= 1 ? blogsPks[0] : null;

    const payload = {
      blog_pk: blogPk,
      pk: data?.pk,
      title: data?.title,
      description: data?.description,
      short_description: data?.short_description,
      punctation: data?.punctation,
      status: data?.status,
      start_date: data?.start_date,
      end_date: data?.end_date,
      content_goal_pks: data?.content_goals.map((goal) => goal.pk),
      is_challenge: challenges() ? data?.is_challenge ?? false : false,
      challenge_start_date: data?.challenge_start_date,
      is_featured: data?.is_featured,
      is_inspiration: data?.is_inspiration,
    };
    mutate(payload);
  };

  return { save, isLoading, isError, isSuccess };
};

export const useSaveProgramBlog = (pk?: PK) => {
  const { mutate, isLoading, isError, isSuccess } = useProgramUpdate();

  const save = (blogsPks: PK[], data?: Program) => {
    if (!pk) {
      return null;
    }

    const blogPk = blogsPks.length >= 1 ? blogsPks[0] : null;

    const payload = {
      blog_pk: blogPk,
      pk: data?.pk,
      title: data?.title,
      description: data?.description,
      short_description: data?.short_description,
      status: data?.status,
      category: data?.category,
    };
    mutate(payload);
  };

  return { save, isLoading, isError, isSuccess };
};

export const useSaveMinisBlog = (pk?: PK) => {
  const { mutate, isLoading, isError, isSuccess } = useMinisUpdate();

  const save = (blogsPks: PK[]) => {
    if (!pk) {
      return null;
    }

    const blogPk = blogsPks.length >= 1 ? blogsPks[0] : null;

    mutate({
      pk: pk,
      blog_pk: blogPk,
    });
  };

  return { save, isLoading, isError, isSuccess };
};

export const useSave = (pk?: PK) => {
  const [created, setCreated] = React.useState(pk);
  const create = useBlogCreate();
  const update = useBlogUpdate();
  const isSaving = create.isLoading || update.isLoading;
  const error = create.error || update.error;

  const save = (values: BlogFields) => {
    const payload = {
      title: values[fields.title.name],
      description: values[fields.description.name],
      url: values[fields.url.name],
      image_url: values[fields.image_url.name],
      is_featured: values[fields.is_featured.name],
      is_inspiration: values[fields.is_inspiration.name],
    };
    if (created) {
      update.mutate({ ...payload, pk: created, status: values.status[0].value });
    } else {
      create.mutate(payload, {
        onSuccess: ({ pk }) => {
          setCreated(pk);
        },
      });
    }
  };

  React.useEffect(() => {
    if (!!pk && created !== pk) {
      setCreated(pk);
    }
  }, [created, pk]);

  return { save, created, isSaving, error };
};
